/**
 * Pillar/category page configuration — drives 7 SEO/AEO/GEO landing pages.
 * Each entry produces:
 *   - <title>, meta description (via usePageSEO)
 *   - On-page H1, intro, sections, internal links
 *   - JSON-LD: WebPage, BreadcrumbList, ItemList (products), FAQPage
 *
 * COMPLIANCE: Every page uses research-only language. No medical claims.
 */

const BASE = "https://www.peptidesformedspas.org";

export const PILLAR_PAGES = {
  // ============================================================
  // PARENT HUB — "Peptides for Med Spas" (top-level category)
  // ============================================================
  "peptides-for-med-spas": {
    slug: "peptides-for-med-spas",
    breadcrumb: "Peptides for Med Spas",
    h1: "Peptides for Med Spas: The Complete Research Catalog",
    eyebrow: "Med Spa Research Hub",
    title: "Peptides for Med Spas | Research Catalog Hub",
    description:
      "The most complete med spa peptide research catalog: anti-aging, weight loss, recovery, hormone, and sexual wellness peptides. Plain-English science, transparent labeling. Not medical advice.",
    keywords: [
      "peptides for med spas", "med spa peptides", "aesthetic peptides", "med spa research peptides",
      "best peptides for med spa", "research peptides for med spas", "peptides used in med spas",
      "research peptides", "med spa catalog"
    ],
    intro:
      "PeptidesForMedSpas is the research-grade catalog and education hub for the peptides most commonly studied in med spa, aesthetic, longevity, and wellness contexts. We organize ten of the most-discussed compounds across five evidence-led categories — anti-aging & skin, weight loss & metabolism, recovery & repair, hormone & longevity, and sexual wellness — with plain-English summaries you can actually use to evaluate marketing claims.",
    sections: [
      {
        heading: "What med spas mean by 'peptides'",
        body:
          "In a med spa context, 'peptides' usually refers to short amino-acid chains used in cosmetic, longevity, or recovery research — molecules like GHK-Cu, glutathione, NAD+, BPC-157, TB-500, sermorelin, and PT-141. These are not the same as FDA-approved prescription drugs. Most are sold strictly as research chemicals for laboratory use only.",
      },
      {
        heading: "How to use this hub",
        body:
          "Pick a category below to see what the actual research examines (versus what marketing claims). Each pillar page links to the matching product pages, evidence overviews, and a curated list of long-form articles. Always discuss any wellness intervention with a licensed clinician.",
      },
      {
        heading: "Why we use research-only language",
        body:
          "Peptide marketing is full of outcome guarantees and 'breakthrough' language that the published evidence does not support. Our editorial standard: describe what compounds are, what studies have looked at, and where evidence is weak — without making therapeutic, aesthetic, or performance claims.",
      },
    ],
    productSlugs: ["ghk-cu", "nad", "5-amino-1mq", "bpc-157", "tb-500", "sermorelin", "mots-c", "pt-141", "klotho"],
    internalLinks: [
      { label: "Anti-Aging Peptides", to: "/anti-aging-peptides" },
      { label: "Weight Loss Peptides", to: "/weight-loss-peptides" },
      { label: "Recovery Peptides", to: "/recovery-peptides" },
      { label: "Hormone Peptides", to: "/hormone-peptides" },
      { label: "Sexual Wellness Peptides", to: "/sexual-wellness-peptides" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
      { label: "Best Peptides for Med Spas (2026 Guide)", to: "/blog/best-peptides-for-med-spas-2026" },
      { label: "α-Klotho and the Biology of Aging (2026)", to: "/blog/alpha-klotho-aging-research-2026" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
    ],
    faqs: [
      {
        q: "What peptides do med spas talk about most?",
        a: "GHK-Cu (copper tripeptide), glutathione, and NAD+ dominate aesthetic and skin conversations. 5-Amino-1MQ shows up in metabolism research talks. BPC-157, TB-500, and Pentadeca Arginate are common in recovery research. Sermorelin and MOTS-c appear in hormone and longevity contexts. PT-141 is the most-discussed compound in sexual wellness pharmacology.",
      },
      {
        q: "Are 'med spa peptides' FDA-approved?",
        a: "Most are not. The vast majority of peptides discussed in med spa marketing — including GHK-Cu, BPC-157, TB-500, MOTS-c, and 5-Amino-1MQ — are classified as research chemicals for laboratory use only. A regulated bremelanotide pharmaceutical exists as an FDA-approved drug for a specific clinical indication; research-only PT-141 sold online is not the same product.",
      },
      {
        q: "Do you sell prescription peptides?",
        a: "No. PeptidesForMedSpas is an educational storefront. We do not operate as a pharmacy or medical provider, and we do not sell prescription medications or controlled substances.",
      },
      {
        q: "How do I evaluate a med spa peptide claim?",
        a: "Identify the exact compound, find the human evidence (not just animal or cell studies), check the regulatory status, and ask a licensed clinician about safety and monitoring. Our 12-question study checklist walks through this step-by-step.",
      },
    ],
  },

  // ============================================================
  // ANTI-AGING & SKIN
  // ============================================================
  "anti-aging-peptides": {
    slug: "anti-aging-peptides",
    breadcrumb: "Anti-Aging Peptides",
    h1: "Anti-Aging Peptide Research: GHK-Cu, Glutathione & NAD+ for Med Spas",
    eyebrow: "Anti-Aging & Skin Research",
    title: "Anti-Aging Peptides | GHK-Cu, Glutathione, NAD+",
    description:
      "Anti-aging research peptides studied in med spa and dermatology contexts: GHK-Cu (copper tripeptide), glutathione, NAD+. Evidence summaries, mechanisms, and red flags. Not medical advice.",
    keywords: [
      "anti-aging peptides", "GHK-Cu copper tripeptide", "glutathione skin", "NAD+ longevity",
      "skin rejuvenation peptides", "collagen peptides research", "med spa anti-aging"
    ],
    intro:
      "The anti-aging shelf is the busiest aisle in any med spa. Three peptides dominate the legitimate research literature: GHK-Cu (a naturally occurring copper-binding tripeptide widely studied in dermatology), glutathione (the body's master intracellular antioxidant), and NAD+ (a coenzyme central to mitochondrial energy and DNA repair). This page summarizes what the studies actually examine — and where claims regularly outrun the evidence.",
    sections: [
      {
        heading: "GHK-Cu: the copper tripeptide",
        body:
          "GHK-Cu is one of the most-studied peptides in cosmetic science. Cell-culture and animal research has examined roles in collagen and elastin gene expression, skin barrier biology, and antioxidant pathways. Real-world cosmetic outcomes depend heavily on formulation, vehicle, and individual factors.",
      },
      {
        heading: "Glutathione: oxidative stress & skin",
        body:
          "Glutathione (Glu-Cys-Gly) is biology textbook material. The skin-aesthetic interest stems from oxidative-stress biology and tyrosinase inhibition pathways. Bioavailability is a real challenge across oral, topical, and injectable routes — outcomes are formulation- and individual-dependent.",
      },
      {
        heading: "NAD+ and longevity",
        body:
          "NAD+ levels decline with age. That observation has driven a wave of longevity research using precursors (NR, NMN) and direct administration. Mechanistic plausibility is strong; long-term human safety and efficacy data is still being established.",
      },
      {
        heading: "Common anti-aging marketing red flags",
        body:
          "Watch for outcome guarantees, before/after photos with no protocol, 'clinically proven' language with no citations, and any product that compares itself to a prescription drug it isn't.",
      },
    ],
    productSlugs: ["ghk-cu", "nad"],
    internalLinks: [
      { label: "GHK-Cu Research Deep Dive", to: "/blog/ghk-cu-copper-peptide-research" },
      { label: "NAD+ Longevity Overview", to: "/blog/nad-longevity-research" },
      { label: "Glutathione Research Overview", to: "/blog/glutathione-skin-research" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
      { label: "Wellness Fundamentals That Beat Hype", to: "/blog/wellness-fundamentals-beat-hype" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
    ],
    faqs: [
      {
        q: "Which anti-aging peptide has the most research?",
        a: "GHK-Cu has the largest dermatology research footprint among the three, with hundreds of cell-culture and animal studies. Glutathione has decades of foundational biochemistry literature but mixed clinical-aesthetic data. NAD+ longevity research is exploding but human clinical data is younger.",
      },
      {
        q: "Is GHK-Cu the same as 'copper peptide' creams in stores?",
        a: "Sometimes. Many cosmetic creams contain GHK-Cu at low concentrations within proprietary blends. Research-grade GHK-Cu sold for laboratory use is a different product class with no formulation framing for human application.",
      },
      {
        q: "Do oral NAD+ pills work?",
        a: "Most consumer 'NAD+' products are precursors (NR or NMN), not NAD+ itself. Whether they produce meaningful clinical outcomes — versus measurable changes in NAD+ tissue levels — is the active research question.",
      },
      {
        q: "Can these peptides replace SPF and retinoids?",
        a: "No. The most evidence-supported anti-aging interventions remain daily broad-spectrum SPF and (when tolerated) retinoids. Peptides are adjuncts — not substitutes for fundamentals.",
      },
    ],
  },

  // ============================================================
  // WEIGHT LOSS & METABOLISM
  // ============================================================
  "weight-loss-peptides": {
    slug: "weight-loss-peptides",
    breadcrumb: "Weight Loss Peptides",
    h1: "Weight Loss Peptide Research: Evidence, Mechanisms & Red Flags for Med Spas",
    eyebrow: "Weight Loss & Metabolism Research",
    title: "Weight Loss Peptides | Evidence & GLP-1 Context",
    description:
      "What the research actually says about weight loss peptides in med spa contexts. 5-Amino-1MQ (NNMT inhibitor), GLP-1 regulatory context, evidence tiers, and marketing red flags. Not medical advice.",
    keywords: [
      "weight loss peptides", "5-Amino-1MQ", "NNMT inhibitor", "GLP-1 peptides", "metabolism peptides",
      "med spa weight loss", "peptides for fat loss", "compounded semaglutide context"
    ],
    intro:
      "Weight-loss and metabolic peptides are the fastest-growing topic in med spa marketing. The legitimate clinical advances (FDA-approved GLP-1 receptor agonists, when prescribed and monitored by a licensed clinician) sit alongside a flood of unverified marketing claims around research-only compounds. This page sorts the categories.",
    complianceCallout: {
      title: "Two product classes, one common name — read carefully",
      columns: [
        {
          eyebrow: "FDA-APPROVED · PRESCRIPTION ONLY",
          heading: "GLP-1 Receptor Agonists",
          body: "Regulated pharmaceuticals (e.g., approved semaglutide and tirzepatide products) with completed RCTs, defined indications, mandatory monitoring, and a licensed-pharmacy supply chain. Dispensed against a clinician order. Not sold on PeptidesForMedSpas.",
          tone: "approved",
        },
        {
          eyebrow: "RESEARCH USE ONLY · NOT FDA-APPROVED",
          heading: "Research Weight-Management Peptides",
          body: "Compounds like 5-Amino-1MQ studied in animal and cell-model literature. Sold for laboratory research only. Not approved for human use. Not interchangeable with the prescription products above.",
          tone: "research",
        },
      ],
      footer: "Mixing these categories — verbally or in marketing — is exactly the pattern that draws regulator scrutiny. Keep them visually and editorially separate.",
    },
    sections: [
      {
        heading: "Three different conversations",
        body:
          "1) FDA-approved GLP-1 receptor agonists for specific clinical indications — these are prescription drugs, monitored by a clinician. 2) Research compounds like 5-Amino-1MQ studied in animal/cell models. 3) Marketing-only 'fat-loss stacks' with no human evidence. These are not interchangeable.",
      },
      {
        heading: "5-Amino-1MQ in research",
        body:
          "5-Amino-1MQ is a small-molecule selective NNMT inhibitor studied in laboratory models examining methylation pathways and adipocyte metabolism. Mechanistic plausibility does not equal proven human weight-loss outcomes. It is a research chemical, not an approved drug.",
      },
      {
        heading: "GLP-1 context (no claims)",
        body:
          "FDA-approved GLP-1 medications are prescription products with documented clinical trial data, monitoring requirements, and known adverse events. PeptidesForMedSpas does not sell or recommend GLP-1 drugs. Discuss medical weight management options with a licensed clinician.",
      },
      {
        heading: "Red flags to refuse",
        body:
          "'30 lbs in 30 days' guarantees, side-by-side photos with no protocol, claims that imply FDA approval where none exists, pressure tactics, and any product that suggests skipping medical supervision.",
      },
    ],
    productSlugs: ["5-amino-1mq"],
    internalLinks: [
      { label: "Weight-Loss Peptides Evidence Tiers", to: "/blog/weight-loss-peptides-evidence" },
      { label: "GLP-1 vs Research Compounds (2026)", to: "/blog/glp-1-vs-research-compounds-2026" },
      { label: "Wellness Fundamentals That Beat Hype", to: "/blog/wellness-fundamentals-beat-hype" },
      { label: "Peptide Marketing Red Flags", to: "/blog/peptide-marketing-red-flags" },
      { label: "Questions to Ask a Clinician", to: "/blog/questions-ask-clinician-peptides" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
    ],
    faqs: [
      {
        q: "Do weight loss peptides actually work?",
        a: "FDA-approved GLP-1 receptor agonists have documented clinical efficacy for specific indications when prescribed and monitored. Research-only compounds like 5-Amino-1MQ have interesting mechanistic data in animal/cell models but no proven human weight-loss outcomes. Marketing-only 'peptide stacks' typically have no supporting evidence at all.",
      },
      {
        q: "Is 5-Amino-1MQ FDA-approved for weight loss?",
        a: "No. 5-Amino-1MQ is a research chemical sold for laboratory study only. It is not approved by the FDA for any human use, and we make no claims regarding weight-loss outcomes.",
      },
      {
        q: "What's the difference between compounded semaglutide and research peptides?",
        a: "Compounded semaglutide is a prescription product prepared by licensed compounding pharmacies under specific FDA rules and clinician oversight. Research peptides sold online are not prescription products, not subject to the same controls, and not approved for human use. We don't sell either category.",
      },
      {
        q: "What should I prioritize before any peptide conversation?",
        a: "Sleep consistency, protein-adequate nutrition, daily activity, and sun protection. These produce larger and more durable changes than any peptide stack — and are still the best first conversation with any clinician.",
      },
    ],
  },

  // ============================================================
  // RECOVERY & REPAIR
  // ============================================================
  "recovery-peptides": {
    slug: "recovery-peptides",
    breadcrumb: "Recovery Peptides",
    h1: "Recovery Peptides: BPC-157, TB-500 & Pentadeca Arginate Research",
    eyebrow: "Recovery & Repair Research",
    title: "Recovery Peptides | BPC-157, TB-500 & PDA",
    description:
      "Research-grade recovery peptides studied in tissue, tendon, and connective-tissue research models: BPC-157, TB-500 (Thymosin Beta-4), Pentadeca Arginate. Evidence summaries and limitations.",
    keywords: [
      "recovery peptides", "BPC-157 research", "TB-500 research", "Thymosin Beta-4",
      "Pentadeca Arginate", "PDA peptide", "tendon healing peptides", "tissue repair research"
    ],
    intro:
      "BPC-157, TB-500, and Pentadeca Arginate (PDA) are the three peptides that show up most often in recovery and tissue-research conversations. The animal-model literature is interesting; the human clinical data is limited. This page lays out what's actually been studied — and where claims regularly outpace the evidence.",
    sections: [
      {
        heading: "BPC-157: gut-derived pentadecapeptide",
        body:
          "BPC-157 is a synthetic 15-amino-acid peptide derived from a protein found in gastric juice. Animal studies have examined effects in tendon, muscle, ligament, and gut research models. Human clinical-trial data is limited; optimal protocols are not established.",
      },
      {
        heading: "TB-500: Thymosin Beta-4 fragment",
        body:
          "TB-500 is a synthetic fragment of Thymosin Beta-4, a naturally occurring peptide involved in actin binding and cell migration. Research has examined wound-healing models, cardiac tissue research, and inflammatory pathways. The compound carries regulatory considerations in athletic contexts.",
      },
      {
        heading: "Pentadeca Arginate (PDA)",
        body:
          "PDA is a 15-amino-acid arginate-salt analog studied for stability properties in connective-tissue research models. Public human data is very limited; most marketing claims significantly exceed what published research supports.",
      },
      {
        heading: "Recovery fundamentals first",
        body:
          "Sleep, nutrition (especially protein adequacy), progressive loading, and skilled physical therapy remain the most evidence-supported recovery interventions. Discuss any peptide research interest with a licensed clinician familiar with your individual history.",
      },
    ],
    productSlugs: ["bpc-157", "tb-500"],
    internalLinks: [
      { label: "Recovery Peptides Research Overview", to: "/blog/recovery-peptides-research" },
      { label: "BPC-157 vs TB-500 (Research Comparison)", to: "/blog/bpc-157-vs-tb-500-research-comparison" },
      { label: "Recovery Guide", to: "/recovery-guide" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
    ],
    faqs: [
      {
        q: "Is BPC-157 FDA-approved for tendon healing?",
        a: "No. BPC-157 is sold as a research chemical for laboratory use only. It is not an FDA-approved drug, and there are no approved human indications for it.",
      },
      {
        q: "What's the difference between TB-500 and Thymosin Beta-4?",
        a: "TB-500 is a synthetic fragment marketed as a research-chemical analog; Thymosin Beta-4 (TB4) is the full naturally occurring peptide that has been the subject of separate clinical research. The terms are often used interchangeably in marketing, which is technically imprecise.",
      },
      {
        q: "Is Pentadeca Arginate (PDA) just a re-branded BPC-157?",
        a: "Not exactly. PDA is described as an arginate-salt analog with different stability properties. Whether that translates to meaningfully different in-vivo behavior in humans has not been established by published clinical data we are aware of.",
      },
      {
        q: "Can recovery peptides be used during athletic training?",
        a: "Many of these compounds are subject to anti-doping regulations and are not approved for human use. Athletes should consult their governing body's prohibited list and a licensed sports physician before any compound use.",
      },
    ],
  },

  // ============================================================
  // HORMONE & LONGEVITY
  // ============================================================
  "hormone-peptides": {
    slug: "hormone-peptides",
    breadcrumb: "Hormone Peptides",
    h1: "Hormone & Longevity Peptides: Sermorelin, MOTS-c & Klotho Research",
    eyebrow: "Hormone & Longevity Research",
    title: "Hormone Peptides | Sermorelin, MOTS-c & Klotho",
    description:
      "Hormone-axis and longevity research peptides and reagents: Sermorelin (GHRH 1-29 analog), MOTS-c (mitochondrial-derived peptide), and Anti-α-Klotho mAb (longevity research antibody). Evidence overviews, regulatory context, and limitations.",
    keywords: [
      "hormone peptides", "sermorelin", "GHRH analog", "growth hormone releasing hormone",
      "MOTS-c", "mitochondrial peptide", "longevity peptides", "med spa hormones",
      "alpha klotho", "klotho protein", "klotho antibody", "klotho aging research"
    ],
    intro:
      "Hormone-axis and longevity research sits at the intersection of endocrinology, mitochondrial biology, and aging science. Three compounds dominate this catalog: Sermorelin (a GHRH 1-29 analog), MOTS-c (a mitochondrial-derived peptide), and Anti-α-Klotho mAb (a research antibody for the aging-suppressor protein α-Klotho). Each is supplied as a research-only material; clinical use of GH-axis compounds belongs in the hands of licensed endocrinologists.",
    sections: [
      {
        heading: "Sermorelin: GHRH 1-29 analog",
        body:
          "Sermorelin is a synthetic 29-amino-acid peptide corresponding to the active fragment of growth-hormone-releasing hormone. It binds GHRH receptors in research models. Endocrine medicine has long studied the GH axis; longevity-clinic narratives often overstate evidence for general anti-aging applications.",
      },
      {
        heading: "MOTS-c: mitochondrial signaling",
        body:
          "MOTS-c is a 16-amino-acid peptide encoded within the mitochondrial 12S rRNA region. It's a real research target in mitochondrial biology and metabolism. Translation to human longevity outcomes is an active research question.",
      },
      {
        heading: "α-Klotho: aging-suppressor protein research",
        body:
          "α-Klotho is one of the most-cited targets in longevity biology — a transmembrane protein discovered in the late 1990s whose levels decline with age and whose over-expression has been associated with extended lifespan in animal models. Anti-α-Klotho monoclonal antibodies are research reagents used in Western blot, IHC, ELISA, and immunoprecipitation workflows examining Klotho biology, FGF23 signaling, and mineral-metabolism research. The antibody is a research tool, not a peptide therapeutic.",
      },
      {
        heading: "Why endocrine context matters",
        body:
          "Any GH-axis intervention warrants monitoring (IGF-1, glucose, etc.) and carries contraindications in conditions like active malignancy. Klotho-related products are research reagents only — not therapeutics. None of these materials are approved for human use.",
      },
    ],
    productSlugs: ["sermorelin", "mots-c", "klotho"],
    internalLinks: [
      { label: "Sermorelin Research Overview", to: "/blog/sermorelin-ghrh-overview" },
      { label: "MOTS-c Mitochondrial Research (2026)", to: "/blog/mots-c-mitochondrial-research-2026" },
      { label: "α-Klotho and the Biology of Aging (2026)", to: "/blog/alpha-klotho-aging-research-2026" },
      { label: "Questions to Ask a Clinician", to: "/blog/questions-ask-clinician-peptides" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
    ],
    faqs: [
      {
        q: "Is Sermorelin the same as HGH?",
        a: "No. Sermorelin is a GHRH analog studied in research models for receptor binding. HGH (human growth hormone) is a separate, regulated pharmaceutical with specific clinical indications. They are not the same product.",
      },
      {
        q: "Does MOTS-c reverse aging?",
        a: "No. That claim outruns the evidence by a wide margin. MOTS-c is studied in mitochondrial biology and metabolism research; clinical translation to human longevity outcomes has not been established.",
      },
      {
        q: "What is α-Klotho?",
        a: "α-Klotho is a transmembrane aging-suppressor protein primarily expressed in kidney, parathyroid, and choroid plexus tissues. It functions as a co-receptor for FGF23 and is one of the most-cited targets in longevity research. Anti-α-Klotho monoclonal antibodies are sold as research reagents — not as therapies.",
      },
      {
        q: "Is hormone peptide research safe for self-experimentation?",
        a: "GH-axis interventions warrant clinical monitoring and have known contraindications. Research-only formulations are not approved for human use, and self-administration carries unknown safety risks.",
      },
      {
        q: "What should I ask an endocrinologist about peptides?",
        a: "Our clinician question checklist covers evidence, safety, monitoring, alternatives, and regulatory status. Bring it to your appointment.",
      },
    ],
  },

  // ============================================================
  // SEXUAL WELLNESS
  // ============================================================
  "sexual-wellness-peptides": {
    slug: "sexual-wellness-peptides",
    breadcrumb: "Sexual Wellness Peptides",
    h1: "Sexual Wellness Peptides: PT-141 (Bremelanotide) Research",
    eyebrow: "Sexual Wellness Pharmacology",
    title: "Sexual Wellness Peptides | PT-141 Research",
    description:
      "PT-141 (bremelanotide) — a melanocortin receptor agonist studied in pharmacology research. Approved-drug vs research-only distinctions, mechanisms, and regulatory context. Not medical advice.",
    keywords: [
      "sexual wellness peptides", "PT-141", "bremelanotide", "melanocortin agonist",
      "MC4R", "research peptide pharmacology", "med spa sexual wellness"
    ],
    intro:
      "Sexual-wellness peptide marketing is dominated by a single compound name: PT-141 (bremelanotide). It's a synthetic cyclic heptapeptide and melanocortin receptor agonist studied in central-nervous-system pharmacology research. It is critical to distinguish the FDA-approved bremelanotide pharmaceutical product from research-only PT-141 sold online — they are not the same thing.",
    sections: [
      {
        heading: "What PT-141 actually is",
        body:
          "PT-141 / bremelanotide is an analog of α-MSH studied at MC3R and MC4R receptors. Research has examined CNS-mediated mechanisms vs peripheral vascular pathways. The receptor pharmacology is well-defined; the clinical translation depends entirely on regulatory status, dose, and route.",
      },
      {
        heading: "Approved drug vs research formulation",
        body:
          "A regulated bremelanotide pharmaceutical product is FDA-approved for a specific clinical indication in adult premenopausal women. It's prescribed by a licensed clinician, manufactured under regulatory controls, and includes documented adverse-event profiles. Research-only 'PT-141' sold online is a different product class entirely — sold for laboratory use, not approved for human use.",
      },
      {
        heading: "Why the distinction matters",
        body:
          "Sexual-wellness conversations often blend the two product classes. They have different quality controls, different known-risk profiles, and different legal contexts. If sexual wellness is the goal, the path runs through a licensed clinician who can evaluate evidence-based options.",
      },
    ],
    productSlugs: ["pt-141"],
    internalLinks: [
      { label: "PT-141 Research Overview", to: "/blog/pt-141-sexual-wellness-research" },
      { label: "PT-141 vs Bremelanotide: Approved Drug vs Research Peptide", to: "/blog/pt-141-vs-bremelanotide-fda-approved-vs-research" },
      { label: "Questions to Ask a Clinician", to: "/blog/questions-ask-clinician-peptides" },
      { label: "Peptide Marketing Red Flags", to: "/blog/peptide-marketing-red-flags" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
    ],
    faqs: [
      {
        q: "Is PT-141 FDA-approved?",
        a: "A regulated bremelanotide pharmaceutical product is FDA-approved for a specific clinical indication in adult premenopausal women. Research-only 'PT-141' sold online — including products in our catalog — is not the same product and is not approved for human use.",
      },
      {
        q: "How does PT-141 / bremelanotide differ from PDE5 inhibitors?",
        a: "PDE5 inhibitors (e.g., the active ingredients in some erectile-dysfunction medications) act peripherally on vascular pathways. PT-141 acts centrally on melanocortin receptors. They are mechanistically distinct compound classes with different evidence and approval contexts.",
      },
      {
        q: "Is PT-141 safe for self-administration?",
        a: "Research-only formulations are not approved for human use. The approved bremelanotide drug has documented adverse events including transient hypertension and nausea, which is one reason clinical use involves prescription and monitoring. Self-administration of research formulations carries unknown safety risks.",
      },
      {
        q: "Can med spas prescribe PT-141?",
        a: "Med spas operate under medical-direction rules that vary by state. Prescription products require a licensed prescriber acting within their scope. Research-only PT-141 sold online is a separate category and is not appropriate for clinical administration.",
      },
    ],
  },

  // ============================================================
  // PEPTIDE GLOSSARY (definitive reference page)
  // ============================================================
  "peptide-glossary": {
    slug: "peptide-glossary",
    breadcrumb: "Peptide Glossary",
    h1: "Peptide Glossary: Definitions for Med Spa & Wellness Audiences",
    eyebrow: "Reference",
    title: "Peptide Glossary | Definitions & Med Spa Reference",
    description:
      "Plain-English definitions for the peptides, classes, mechanisms, and regulatory terms most often used in med spa, aesthetic, and longevity research conversations.",
    keywords: [
      "peptide glossary", "peptide definitions", "peptide terms", "med spa terminology",
      "peptide reference", "GHRH definition", "melanocortin definition"
    ],
    intro:
      "A reference page for the terms that show up across this site. Where helpful, each entry links out to a deeper article or product page. All definitions are educational; nothing on this page is medical advice.",
    sections: [
      {
        heading: "Compound definitions",
        body:
          "GHK-Cu — naturally occurring copper-binding tripeptide (Gly-His-Lys + Cu²⁺) widely studied in dermatology research. Glutathione — naturally occurring tripeptide antioxidant (Glu-Cys-Gly); central to redox biology. NAD+ — coenzyme central to mitochondrial energy metabolism, DNA repair, and sirtuin activity. 5-Amino-1MQ — small-molecule selective NNMT inhibitor studied in metabolism research. BPC-157 — synthetic 15-amino-acid peptide derived from a gastric-juice protein. TB-500 — synthetic fragment of Thymosin Beta-4. Pentadeca Arginate (PDA) — 15-amino-acid arginate-salt analog. Sermorelin — synthetic 29-amino-acid GHRH 1-29 analog. MOTS-c — 16-amino-acid mitochondrial-derived peptide. PT-141 (bremelanotide) — synthetic cyclic heptapeptide, melanocortin receptor agonist.",
      },
      {
        heading: "Mechanism & receptor terms",
        body:
          "GHRH receptor — receptor for growth-hormone-releasing hormone; bound by sermorelin and other GHRH analogs. NNMT — nicotinamide N-methyltransferase, an enzyme in methylation/metabolism research. Melanocortin receptors (MC3R/MC4R) — central-nervous-system receptors targeted by α-MSH analogs including bremelanotide. Sirtuins — NAD+-dependent deacetylases studied in aging biology. PARP — DNA-repair enzymes that consume NAD+.",
      },
      {
        heading: "Regulatory & study terms",
        body:
          "Research chemical — compound sold for laboratory research only; not approved for human use. FDA-approved drug — pharmaceutical product approved by the U.S. Food and Drug Administration for a specific clinical indication. RCT — randomized controlled trial, the strongest design for demonstrating clinical efficacy. Off-label — clinical use of an approved drug for an indication other than the one for which it was approved. Compounded — a preparation made by a licensed compounding pharmacy under specific FDA rules.",
      },
      {
        heading: "Form & quality terms",
        body:
          "Lyophilized — freeze-dried form most peptides ship in. COA — Certificate of Analysis documenting identity and purity. HPLC — high-performance liquid chromatography, an analytical purity method. MS — mass spectrometry, used to confirm molecular identity. Bioavailability — fraction of a compound that reaches systemic circulation in active form (highly variable between routes).",
      },
    ],
    productSlugs: ["ghk-cu", "nad", "5-amino-1mq", "bpc-157", "tb-500", "sermorelin", "mots-c", "pt-141", "klotho"],
    internalLinks: [
      { label: "Peptides for Med Spas (Hub)", to: "/peptides-for-med-spas" },
      { label: "Anti-Aging Peptides", to: "/anti-aging-peptides" },
      { label: "Weight Loss Peptides", to: "/weight-loss-peptides" },
      { label: "Recovery Peptides", to: "/recovery-peptides" },
      { label: "Hormone Peptides", to: "/hormone-peptides" },
      { label: "Sexual Wellness Peptides", to: "/sexual-wellness-peptides" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
      { label: "Peptide Dosing Calculator (DoseMyPeptides.com ↗)", to: "https://dosingmypeptides.com/", external: true },
    ],
    faqs: [
      {
        q: "What's the difference between a peptide and a protein?",
        a: "Convention: peptides have 2-50 amino acids, proteins have 50+ and typically fold into complex 3D structures. The line is somewhat arbitrary and varies by source.",
      },
      {
        q: "What does 'lyophilized' mean?",
        a: "Lyophilized = freeze-dried. Most research peptides ship as a lyophilized powder for stability and are reconstituted for laboratory use.",
      },
      {
        q: "What is a Certificate of Analysis (COA)?",
        a: "A COA is a document that reports the identity (often via mass spectrometry) and purity (often via HPLC) of a specific batch of a compound. Reputable suppliers make COAs available on request.",
      },
      {
        q: "What does 'research-only' actually mean?",
        a: "Sold for laboratory research and analytical purposes; not for human or animal use; not evaluated by the FDA; not intended to diagnose, treat, cure, or prevent any disease.",
      },
    ],
  },
};

export const PILLAR_SLUGS = Object.keys(PILLAR_PAGES);

export function buildPillarJsonLd(page, products) {
  const pageUrl = `${BASE}/${page.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: page.title,
      description: page.description,
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      about: { "@id": `${BASE}/#organization` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", ".pillar-intro"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: page.breadcrumb, item: pageUrl },
      ],
    },
    products && products.length > 0 && {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${pageUrl}#itemlist`,
      name: page.h1,
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          description: p.description,
          url: `${BASE}/peptides/${p.slug}`,
          category: p.category,
        },
      })),
    },
    page.faqs && page.faqs.length > 0 && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ].filter(Boolean);
}
